import {
  DepreciationItem,
  depreciationTable,
} from '../pages/apk/models/depreciation-table.class';

/** Upper bound of the GAP top-up shown in the homepage payout simulation. */
export const GAP_PAYOUT_CAP = 150000;

/**
 * Homepage depreciation rows store only `priceDecrease`.
 * `beginningValue` is filled later by `DepreciationItemC` (`1 - priceDecrease`).
 * The homepage calculator reads the raw table, so the ratio has to be derived here.
 */
export function remainingValueRatio(item: DepreciationItem): number | null {
  if (
    typeof item.beginningValue === 'number' &&
    Number.isFinite(item.beginningValue)
  ) {
    return item.beginningValue;
  }

  if (
    typeof item.priceDecrease === 'number' &&
    Number.isFinite(item.priceDecrease)
  ) {
    return 1 - item.priceDecrease;
  }

  return null;
}

/** Finite number suitable for Angular `DecimalPipe`. Rejects `NaN` and the string `"NaN"`. */
export function finiteAmount(value: unknown): number | null {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null;
  }

  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }

  return null;
}

/**
 * Insurer payout (AC/OC) after `vehicleAge` months.
 * Returns a rounded zł amount, or `null` when the age is outside the table.
 */
export function getVehiclePriceHomePage(
  vehicleAge: number,
  vehiclePriceFromInsurance: number,
  table: DepreciationItem[] = depreciationTable,
): number | null {
  const price = finiteAmount(vehiclePriceFromInsurance);
  if (price == null) {
    return null;
  }

  const tableItem = table.find((item) => item.month === vehicleAge);
  if (!tableItem) {
    return vehicleAge === 0 ? Math.round(price) : null;
  }

  const ratio = remainingValueRatio(tableItem);
  if (ratio == null) {
    return vehicleAge === 0 ? Math.round(price) : null;
  }

  const payout = Math.round(price * ratio);
  return Number.isFinite(payout) ? payout : null;
}

/** GAP top-up: purchase price minus insurer payout, capped at {@link GAP_PAYOUT_CAP}. */
export function gapPayout(
  purchasePrice: number,
  insurerPayout: number,
): number | null {
  const price = finiteAmount(purchasePrice);
  const insurer = finiteAmount(insurerPayout);
  if (price == null || insurer == null) {
    return null;
  }

  const difference = price - insurer;
  if (!Number.isFinite(difference)) {
    return null;
  }

  return difference < GAP_PAYOUT_CAP ? difference : GAP_PAYOUT_CAP;
}
